// src/components/PlayerCard.js
import React from 'react';
import './PlayerCard.css';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, CheckCircle2 } from 'lucide-react';
import TeamBadge from './TeamBadge';
import { MANTRA_ROLE_THEME } from './auction/MantraRoleConfiguration';
import { normalizeNameText, splitRoleTokens } from './auction/auctionRosterUtils';

const ROLE_THEME = {
  P: { bg: 'rgba(245,197,66,.14)',  border: 'rgba(245,197,66,.32)',  color: '#92650a' },
  D: { bg: 'rgba(32,197,94,.12)',   border: 'rgba(32,197,94,.28)',   color: '#14532d' },
  C: { bg: 'rgba(59,130,246,.12)',  border: 'rgba(59,130,246,.28)',  color: '#1e3a8a' },
  A: { bg: 'rgba(239,68,68,.12)',   border: 'rgba(239,68,68,.28)',   color: '#7f1d1d' },
};

function roleTheme(role) {
  return ROLE_THEME[role] || { bg: 'rgba(148,163,184,.1)', border: 'rgba(148,163,184,.22)', color: 'var(--muted)' };
}

function fmt(value, dec = 2) {
  const n = Number(value);
  return Number.isFinite(n) ? (dec > 0 ? n.toFixed(dec) : String(Math.round(n))) : '—';
}

function Divider() {
  return <span className="pc-divider" aria-hidden="true" />;
}

function StatPill({ label, value, accent, highlighted = false }) {
  return (
    <div className={`pc-statpill pc-statpill--${accent}${highlighted ? ' pc-statpill--sorted' : ''}`}>
      <span className="pc-statpill__val">{value}</span>
      <span className="pc-statpill__lbl">{label}</span>
    </div>
  );
}

// Quale statistica della riga evidenziare per ciascun ordinamento attivo.
const SORT_HIGHLIGHT = {
  value_desc: 'value',
  markavg_desc: 'mv',
  fmarkavg_desc: 'fv',
  caps_desc: 'caps',
  goals_desc: 'goals',
  goals_against_desc: 'gotgoals',
  assists_desc: 'assists',
  saves_desc: 'saves',
};

function getMantraTokens(player, mantraRoleMap) {
  const raw = mantraRoleMap?.[normalizeNameText(player?.name || '')] || '';
  const tokens = splitRoleTokens(raw);
  if (tokens.length) return tokens;
  // I portieri non sono nel config Mantra → ruolo Mantra = "P".
  return player?.role === 'P' ? ['P'] : [];
}

export default function PlayerCard({
  player,
  allPlayers = [],
  mantraRoleMap = null,
  selected = false,
  onToggleCompare,
  sortKey = '',
}) {
  const navigate = useNavigate();
  const theme    = roleTheme(player.role);
  const isKeeper = player.role === 'P';
  const valuation = Number(player.value || 0);
  const mantraTokens = getMantraTokens(player, mantraRoleMap);

  const mainVal   = isKeeper ? (player.gotgoals ?? player.goalsConceded ?? 0) : (player.goals   || 0);
  const mainLbl   = isKeeper ? 'GS'  : 'Gol';
  const secondVal = isKeeper ? (player.spenalties ?? player.penaltiesSaved ?? player.saves ?? 0) : (player.assists || 0);
  const secondLbl = isKeeper ? 'Par' : 'Ast';

  const highlight = SORT_HIGHLIGHT[sortKey] || null;

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleCompare?.(player);
  };

  return (
    <article
      className={`player-card ${selected ? 'is-selected' : ''}`}
      style={{ '--role-bg': theme.bg, '--role-border': theme.border, '--role-color': theme.color }}
      onClick={() => navigate(`/players/${player.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/players/${player.id}`); }}
    >
      <div className="pc-left">
        <span className="pc-role">{player.role || '—'}</span>
        {/* Slot a larghezza fissa: logo e nome restano incolonnati anche se il
            ruolo Mantra ha 1, 2 o 3 token (o nessuno). */}
        <span className="pc-mroles" title={mantraTokens.length ? 'Ruolo Mantra' : undefined}>
          {mantraTokens.map((t) => {
            const th = MANTRA_ROLE_THEME[t] || MANTRA_ROLE_THEME.Dc;
            return (
              <span
                key={t}
                className="pc-mrole"
                style={{ background: th.bg, border: `1px solid ${th.border}`, color: th.color }}
              >
                {t}
              </span>
            );
          })}
        </span>
        <TeamBadge team={player.team} size={30} />
        <div className="pc-nameblock">
          <strong className="pc-name">{player.name}</strong>
          <span className="pc-team">{player.team || '—'}</span>
        </div>
      </div>

      <div className="pc-right">
        <StatPill label="Quota" value={fmt(valuation, 0)}        accent="neutral" highlighted={highlight === 'value'} />
        <Divider />
        <StatPill label="MV"   value={fmt(player.markavg,  2)}   accent="blue"    highlighted={highlight === 'mv'} />
        <StatPill label="FV"   value={fmt(player.fmarkavg, 2)}   accent="violet"  highlighted={highlight === 'fv'} />
        <Divider />
        <StatPill label="PG"   value={player.caps || 0}          accent="teal"    highlighted={highlight === 'caps'} />
        <Divider />
        <StatPill
          label={mainLbl}
          value={mainVal}
          accent={isKeeper ? 'red' : 'green'}
          highlighted={isKeeper ? highlight === 'gotgoals' : highlight === 'goals'}
        />
        <StatPill
          label={secondLbl}
          value={secondVal}
          accent={isKeeper ? 'teal' : 'amber'}
          highlighted={isKeeper ? highlight === 'saves' : highlight === 'assists'}
        />

        <button
          type="button"
          className={`pc-compare ${selected ? 'is-active' : ''}`}
          onClick={stop}
          aria-label={selected ? 'Rimuovi dal confronto' : 'Aggiungi al confronto'}
        >
          {selected ? <CheckCircle2 size={15} /> : <PlusCircle size={15} />}
        </button>
      </div>
    </article>
  );
}
